<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import BranchesAPI from 'dashboard/api/branches';
import CountriesAPI from 'dashboard/api/countries';
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';

const props = defineProps({
  resourceType: {
    type: String,
    required: true,
    validator: value => ['country', 'branch'].includes(value),
  },
});

const { t } = useI18n();
const { isAdmin } = useAdmin();
const store = useStore();
const agents = useMapGetter('agents/getAgents');
const manageableTeams = useMapGetter('teams/getManageableTeams');

const records = ref([]);
const countries = ref([]);
const branches = ref([]);
const isLoading = ref(false);
const isSaving = ref(false);
const showForm = ref(false);
const showDelete = ref(false);
const selectedRecord = ref(null);
const form = reactive({
  name: '',
  leaderId: '',
  countryId: '',
});

const isCountry = computed(() => props.resourceType === 'country');
const translationRoot = computed(() =>
  isCountry.value ? 'COUNTRIES_SETTINGS' : 'BRANCHES_SETTINGS'
);
const api = computed(() => (isCountry.value ? CountriesAPI : BranchesAPI));

const resetForm = () => {
  form.name = '';
  form.leaderId = '';
  form.countryId = '';
  selectedRecord.value = null;
};

const fetchCountries = async () => {
  const { data } = await CountriesAPI.get();
  countries.value = data;
};

const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const { data } = await api.value.get();
    records.value = data;
    if (isCountry.value) {
      const { data: branchRecords } = await BranchesAPI.get();
      branches.value = branchRecords;
    } else {
      await fetchCountries();
    }
    await store.dispatch('teams/get');
  } catch {
    useAlert(t(`${translationRoot.value}.API.ERROR`));
  } finally {
    isLoading.value = false;
  }
};

const openCreate = () => {
  resetForm();
  showForm.value = true;
};

const openEdit = record => {
  selectedRecord.value = record;
  form.name = record.name;
  form.leaderId = record.leader_id || '';
  form.countryId = record.country_id || '';
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const resourcePayload = computed(() => {
  const payload = {
    name: form.name.trim(),
    leader_id: form.leaderId || null,
  };
  if (!isCountry.value) payload.country_id = form.countryId;
  return { [props.resourceType]: payload };
});

const canSubmit = computed(
  () =>
    form.name.trim().length > 0 &&
    (isCountry.value || String(form.countryId).length > 0)
);

const saveRecord = async () => {
  if (!canSubmit.value) return;
  isSaving.value = true;
  try {
    if (selectedRecord.value) {
      await api.value.update(selectedRecord.value.id, resourcePayload.value);
    } else {
      await api.value.create(resourcePayload.value);
    }
    useAlert(t(`${translationRoot.value}.API.SUCCESS`));
    closeForm();
    await fetchRecords();
  } catch {
    useAlert(t(`${translationRoot.value}.API.ERROR`));
  } finally {
    isSaving.value = false;
  }
};

const openDelete = record => {
  selectedRecord.value = record;
  showDelete.value = true;
};

const closeDelete = () => {
  showDelete.value = false;
  selectedRecord.value = null;
};

const deleteRecord = async () => {
  try {
    await api.value.delete(selectedRecord.value.id);
    useAlert(t(`${translationRoot.value}.API.DELETE_SUCCESS`));
    closeDelete();
    await fetchRecords();
  } catch {
    useAlert(t(`${translationRoot.value}.API.ERROR`));
  }
};

const recordSubtitle = record => {
  if (isCountry.value) {
    return t('COUNTRIES_SETTINGS.LIST.COUNTS', {
      branches: record.branches_count,
      teams: record.teams_count,
    });
  }
  return t('BRANCHES_SETTINGS.LIST.COUNTRY', {
    country: record.country.name,
    teams: record.teams_count,
  });
};

const branchesForCountry = countryId =>
  branches.value.filter(branch => branch.country_id === countryId);

const teamsForBranch = branchId =>
  manageableTeams.value.filter(team => team.branch_id === branchId);

watch(() => props.resourceType, fetchRecords);

onMounted(() => {
  if (isAdmin.value) store.dispatch('agents/get');
  fetchRecords();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t(`${translationRoot}.LOADING`)"
    :no-records-found="!records.length"
    :no-records-message="$t(`${translationRoot}.LIST.EMPTY`)"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t(`${translationRoot}.HEADER`)"
        :description="$t(`${translationRoot}.DESCRIPTION`)"
        feature-name="team_management"
      >
        <template #actions>
          <Button
            v-if="isAdmin"
            :label="$t(`${translationRoot}.NEW`)"
            size="sm"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="record in records"
          :key="record.id"
          class="flex items-center justify-between gap-4 py-4"
        >
          <div class="min-w-0">
            <h3 class="mb-1 text-heading-3 text-n-slate-12">
              {{ record.name }}
            </h3>
            <p class="mb-1 text-body-main text-n-slate-11">
              {{ recordSubtitle(record) }}
            </p>
            <p class="mb-0 text-body-main text-n-slate-11">
              {{
                record.leader
                  ? $t(`${translationRoot}.LIST.LEADER`, {
                      name: record.leader.name,
                    })
                  : $t(`${translationRoot}.LIST.NO_LEADER`)
              }}
            </p>
            <div
              v-if="isCountry"
              class="mt-3 flex flex-col gap-2 border-s border-n-weak ps-4"
            >
              <div
                v-for="branch in branchesForCountry(record.id)"
                :key="branch.id"
                class="flex flex-col gap-1"
              >
                <span class="text-body-main font-medium text-n-slate-12">
                  {{ branch.name }}
                </span>
                <div class="flex flex-wrap gap-1.5">
                  <span
                    v-for="team in teamsForBranch(branch.id)"
                    :key="team.id"
                    class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
                  >
                    {{ team.name }}
                  </span>
                  <span
                    v-if="!teamsForBranch(branch.id).length"
                    class="text-xs text-n-slate-10"
                  >
                    {{ $t('COUNTRIES_SETTINGS.LIST.NO_TEAMS') }}
                  </span>
                </div>
              </div>
              <span
                v-if="!branchesForCountry(record.id).length"
                class="text-xs text-n-slate-10"
              >
                {{ $t('COUNTRIES_SETTINGS.LIST.NO_BRANCHES') }}
              </span>
            </div>
            <div v-else class="mt-3 flex flex-wrap gap-1.5">
              <span
                v-for="team in teamsForBranch(record.id)"
                :key="team.id"
                class="rounded-md bg-n-alpha-2 px-2 py-1 text-xs text-n-slate-11"
              >
                {{ team.name }}
              </span>
              <span
                v-if="!teamsForBranch(record.id).length"
                class="text-xs text-n-slate-10"
              >
                {{ $t('BRANCHES_SETTINGS.LIST.NO_TEAMS') }}
              </span>
            </div>
          </div>
          <div v-if="isAdmin" class="flex flex-shrink-0 gap-2">
            <Button
              icon="i-lucide-pencil"
              color="slate"
              variant="ghost"
              size="sm"
              @click="openEdit(record)"
            />
            <Button
              icon="i-lucide-trash-2"
              color="ruby"
              variant="ghost"
              size="sm"
              @click="openDelete(record)"
            />
          </div>
        </div>
      </div>
    </template>

    <woot-modal v-model:show="showForm" :on-close="closeForm">
      <div class="flex flex-col gap-5 p-8">
        <woot-modal-header
          :header-title="
            $t(
              `${translationRoot}.FORM.${
                selectedRecord ? 'EDIT_TITLE' : 'CREATE_TITLE'
              }`
            )
          "
        />
        <label class="flex flex-col gap-2 text-sm text-n-slate-12">
          {{ $t(`${translationRoot}.FORM.NAME`) }}
          <input
            v-model="form.name"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 outline-none focus:border-n-brand"
            type="text"
          />
        </label>
        <label
          v-if="!isCountry"
          class="flex flex-col gap-2 text-sm text-n-slate-12"
        >
          {{ $t('BRANCHES_SETTINGS.FORM.COUNTRY') }}
          <select
            v-model="form.countryId"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 outline-none focus:border-n-brand"
          >
            <option value="" disabled>
              {{ $t('BRANCHES_SETTINGS.FORM.SELECT_COUNTRY') }}
            </option>
            <option
              v-for="country in countries"
              :key="country.id"
              :value="country.id"
            >
              {{ country.name }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-2 text-sm text-n-slate-12">
          {{ $t(`${translationRoot}.FORM.LEADER`) }}
          <select
            v-model="form.leaderId"
            class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 outline-none focus:border-n-brand"
          >
            <option value="">
              {{ $t(`${translationRoot}.FORM.NO_LEADER`) }}
            </option>
            <option v-for="agent in agents" :key="agent.id" :value="agent.id">
              {{ agent.name }}
            </option>
          </select>
        </label>
        <div class="flex justify-end gap-2">
          <Button
            :label="$t(`${translationRoot}.FORM.CANCEL`)"
            color="slate"
            variant="ghost"
            @click="closeForm"
          />
          <Button
            :label="$t(`${translationRoot}.FORM.SAVE`)"
            :disabled="!canSubmit"
            :is-loading="isSaving"
            @click="saveRecord"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDelete"
      :on-close="closeDelete"
      :on-confirm="deleteRecord"
      :title="$t(`${translationRoot}.DELETE.TITLE`)"
      :message="$t(`${translationRoot}.DELETE.MESSAGE`)"
      :message-value="selectedRecord?.name || ''"
      :confirm-text="$t(`${translationRoot}.DELETE.CONFIRM`)"
      :reject-text="$t(`${translationRoot}.FORM.CANCEL`)"
    />
  </SettingsLayout>
</template>
